import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Icon,
  Popper,
  TextField,
  Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useContext,
} from 'react';
import { AuthContext } from '../../../shared/context/AuthContext';
import { slugify } from '../../utils/StringFormat';

const useStyles = makeStyles((theme: Theme) => ({
  paperBg: {
    backgroundColor: theme.palette.background.shadedown_10,
  },
  visitorList: {
    backgroundColor: theme.palette.background.default,
  },
  parentText: {
    maxHeight: 60,
    overflow: 'hidden',
  },
  pinField: {
    width: 80,
  },
}));

interface OpenProps {
  topic: any;
  callback: () => void;
  postsWithoutTopic: number;
}

export const DiscussionTopicEdit = forwardRef((props, ref) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [postError, setPostError] = useState(null);
  const [data, setData] = useState(null);
  const [deleteMessages, setDeleteMessages] = useState(false);
  const [connectPosts, setConnectPosts] = useState(false);

  useImperativeHandle(ref, () => ({
    open: ({ topic, callback, postsWithoutTopic }: OpenProps) => {
      setData({ topic, callback, postsWithoutTopic });
      setOpen(true);
    },
  }));

  const cancelHandler = () => {
    setPostError(null);
    setData(null);
    setOpen(false);
    setDeleteMessages(false);
    setConnectPosts(false);
  };
  const deleteHandler = () => {
    axios
      .delete(
        `/api/visitor/discussiontopics/${data.topic.id}?deletemessages=${
          deleteMessages ? 1 : 0
        }`
      )
      .then(() => {
        cancelHandler();
        data.callback && data.callback(null);
      })
      .catch((error) => {
        setPostError(error.response.data.message);
      });
  };
  const save = () => {
    if (!data.topic.title || !data.topic.slug) {
      return setPostError('Title & slug is required');
    }

    if (data.topic.id) {
      axios
        .put(`/api/visitor/discussiontopics/${data.topic.id}`, {
          ...data.topic,
          connectPosts,
        })
        .then((response) => {
          cancelHandler();
          data.callback && data.callback(response.data);
        })
        .catch((error) => {
          setPostError(error.response.data.message);
        });
    } else {
      axios
        .post(
          `/api/visitor/conferences/${window.CONFERENCE_ID}/discussiontopics`,
          { ...data.topic, connectPosts }
        )
        .then((response) => {
          data.callback && data.callback(response.data);
          cancelHandler();
        })
        .catch((error) => {
          setPostError(error.response.data.message);
        });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((currentData: any) => {
      currentData.topic[name] = value;

      if (name === 'title') {
        currentData.topic.slug = slugify(value);
      }
      return { ...currentData };
    });
  };

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return false;
        }
        cancelHandler(event);
      }}
      fullWidth={true}
      maxWidth="md"
      aria-labelledby="Answer"
      PaperProps={{ className: classes.paperBg }}
    >
      {data && (
        <>
          <DialogTitle>{data.id ? 'Edit ' : 'New '} Topic</DialogTitle>
          <DialogContent>
            <Box pb={1}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                type="text"
                name="title"
                label="Title (mandatory)"
                placeholder="A clear and informative title"
                variant="outlined"
                onChange={handleInputChange}
                value={data.topic.title || ''}
              />
            </Box>

            <Box pb={1}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                type="text"
                name="slug"
                label="Slug (mandatory)"
                placeholder="No spaces or weird characters"
                variant="outlined"
                onChange={handleInputChange}
                value={data.topic.slug || ''}
              />
            </Box>

            <Box pb={1}>
              <TextField
                multiline={true}
                rowsMax={3}
                InputLabelProps={{ shrink: true }}
                fullWidth
                type="text"
                name="description"
                label="Description"
                placeholder="What is this topic all about?"
                variant="outlined"
                onChange={handleInputChange}
                value={data.topic.description || ''}
              />
            </Box>
            <Box pb={1}>
              <TextField
                value={data.topic.poststate}
                onChange={handleInputChange}
                inputProps={{ 'data-forname': 'poststate' }}
                name="poststate"
                label="How can people post?"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="open">Open for all</option>
                <option value="suggest">Suggest (accept by moderator)</option>
                <option value="closed">Closed (only for moderator)</option>
              </TextField>

              <TextField
                value={data.topic.viewstate}
                onChange={handleInputChange}
                inputProps={{ 'data-forname': 'viewstate' }}
                name="viewstate"
                label="Visibile?"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Visible</option>
                <option value="moderators">Hidden</option>
              </TextField>
              <Box p={1} display="inline-block">
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={data.topic.ai}
                      onChange={(e) =>
                        setData((currentData: any) => {
                          currentData.topic.ai = e.target.checked;
                          return { ...currentData };
                        })
                      }
                    />
                  }
                  label="AI Chatbot"
                />
              </Box>
              {data.postsWithoutTopic > 0 && (
                <Box p={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={connectPosts}
                        onChange={() => setConnectPosts(!connectPosts)}
                      />
                    }
                    label={'Move all posts without topic to this?'}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Box display="flex" className={classes.buttonList} flexGrow={1}>
              <Box flexGrow={1} display="flex" alignItems="center">
                {data.topic && data.topic.id && (
                  <>
                    <Button
                      onClick={() =>
                        window.confirm(
                          'Really delete discussion and all replies?'
                        ) && deleteHandler()
                      }
                      startIcon={<Icon>delete</Icon>}
                    >
                      Delete
                    </Button>
                    <br />
                    <Box p={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={deleteMessages}
                            onChange={() => setDeleteMessages(!deleteMessages)}
                          />
                        }
                        label={'Delete all topic posts'}
                      />
                    </Box>
                  </>
                )}
              </Box>
              {postError && (
                <Box align="right" color="error.main">
                  {postError}
                </Box>
              )}
              <Box flexGrow={1} align="right">
                <Button onClick={cancelHandler}>Cancel</Button>

                <Button
                  color="primary"
                  onClick={save}
                  disabled={
                    data.topic.title.length === 0 ||
                    data.topic.slug.length === 0 ||
                    data.topic.slug === 'all'
                  }
                >
                  Save
                </Button>
              </Box>
            </Box>
          </DialogActions>
          <DialogContent>
            <ul>
              <li>The top topic will be used as default for New Post.</li>
              <li>
                Use post "suggest" to let others post suggestions that you
                (moderators) can confirm.
              </li>
              <li>Set visibility to hidden for a secret moderator forum.</li>
              <li>
                You can specify topic in Expo room forum to group all into one.
              </li>
            </ul>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
});

DiscussionTopicEdit.displayName = 'DiscussionTopicEdit';
